import { useState, useMemo, type ElementType } from 'react';
import { marked } from 'marked';
import { PageHeader } from '@ui/components/PageHeader';
import { BookOpen, FileText, Settings, Shield, Cloud, Download, AlertCircle, Search } from 'lucide-react';

import userGuideMain from '../../../docs/user-guide.md?raw';
import setupGuide from '../../../docs/setup-and-configuration-guide.md?raw';
import { HELP_CONTENT } from '@app/help/helpContent';

interface HelpTopic {
  readonly id: string;
  readonly label: string;
  readonly icon: ElementType;
  readonly content: string;
}

const TOPICS: HelpTopic[] = [
  { id: 'user-guide', label: 'User Guide', icon: BookOpen, content: userGuideMain },
  { id: 'setup', label: 'Setup & Configuration', icon: Settings, content: setupGuide },
  { id: 'invoices', label: 'Invoices', icon: FileText, content: HELP_CONTENT.invoices },
  { id: 'quotations', label: 'Quotations', icon: FileText, content: HELP_CONTENT.quotations },
  { id: 'sales-orders', label: 'Sales Orders', icon: FileText, content: HELP_CONTENT['sales-orders'] },
  { id: 'delivery-notes', label: 'Delivery Notes', icon: FileText, content: HELP_CONTENT['delivery-notes'] },
  { id: 'credit-notes', label: 'Credit Notes', icon: FileText, content: HELP_CONTENT['credit-notes'] },
  { id: 'sales-returns', label: 'Sales Returns', icon: FileText, content: HELP_CONTENT['sales-returns'] },
  { id: 'purchase-orders', label: 'Purchase Orders', icon: FileText, content: HELP_CONTENT['purchase-orders'] },
  { id: 'products', label: 'Products & Services', icon: FileText, content: HELP_CONTENT.products },
  { id: 'customers', label: 'Customers', icon: FileText, content: HELP_CONTENT.customers },
  { id: 'suppliers', label: 'Suppliers', icon: FileText, content: HELP_CONTENT.suppliers },
  { id: 'payments', label: 'Payments & Receipts', icon: FileText, content: HELP_CONTENT.payments },
  { id: 'business-profile', label: 'Business Profile', icon: Shield, content: HELP_CONTENT['business-profile'] },
  { id: 'backup-restore', label: 'Backup & Restore', icon: Download, content: HELP_CONTENT['backup-restore'] },
  { id: 'sync-cloud', label: 'Cloud Sync', icon: Cloud, content: HELP_CONTENT['sync-cloud'] },
];

const TOPIC_BY_ID = new Map(TOPICS.map((t) => [t.id, t]));

function renderHtml(markdown: string): string {
  return marked.parse(markdown, { async: false }) as string;
}

export function HelpScreen() {
  const [search, setSearch] = useState('');
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);

  const filteredTopics = useMemo(() => {
    if (!search.trim()) return TOPICS;
    const q = search.toLowerCase();
    return TOPICS.filter(
      (t) => t.label.toLowerCase().includes(q) || t.content.toLowerCase().includes(q),
    );
  }, [search]);

  const hasSearchResults = search.trim().length > 0;
  const showTopicList = !hasSearchResults || filteredTopics.length > 1;
  const autoOpenSingleResult = hasSearchResults && filteredTopics.length === 1 && !activeTopicId;

  const activeTopic = activeTopicId ? TOPIC_BY_ID.get(activeTopicId) : null;
  const displayTopic = activeTopic ?? (autoOpenSingleResult ? filteredTopics[0] : null);
  const renderHtmlString = displayTopic ? renderHtml(displayTopic.content) : '';

  return (
    <section data-testid="help-screen" className="max-w-4xl">
      <PageHeader
        title="Help"
        description="Guides and documentation for DNR — Digital Net Register. All help is bundled in the app — no internet required."
      />

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" aria-hidden />
          <input
            type="search"
            placeholder="Search help topics…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value === '') setActiveTopicId(null);
            }}
            className="w-full rounded border border-slate-700 bg-slate-900 px-8 py-2 text-sm text-slate-100 placeholder-slate-500"
            aria-label="Search help topics"
          />
        </div>
      </div>

      {hasSearchResults && filteredTopics.length === 0 ? (
        <div className="rounded-lg border border-slate-800 p-4 text-sm text-slate-400">
          <AlertCircle className="h-4 w-4 inline mr-2" aria-hidden />
          No help topics found for "{search}".
        </div>
      ) : null}

      {showTopicList ? (
        autoOpenSingleResult ? null : (
          <div className="grid gap-3 sm:grid-cols-2">
            {filteredTopics.map((topic) => {
              const Icon = topic.icon;
              return (
                <button
                  key={topic.id}
                  onClick={() => setActiveTopicId(topic.id)}
                  className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900 p-4 text-left transition-colors hover:border-brand-700 hover:bg-slate-800"
                >
                  <Icon className="h-5 w-5 text-brand-400 shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <span className="block text-sm font-semibold text-slate-100">{topic.label}</span>
                    <span className="text-xs text-slate-500">Click to read the guide</span>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : null}

      {renderHtmlString ? (
        <div className="mt-4">
          {!hasSearchResults ? (
            <button
              onClick={() => setActiveTopicId(null)}
              className="mb-3 text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2"
            >
              &larr; Back to all topics
            </button>
          ) : null}
          <div
            className="prose prose-invert prose-sm max-w-none text-slate-300"
            dangerouslySetInnerHTML={{ __html: renderHtmlString }}
          />
        </div>
      ) : null}
    </section>
  );
}
